import useRecentSearches from '@hooks/search/use-recent-searches';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CatalogContext } from '@context/catalog.context';
import { useContextSelector } from 'use-context-selector';
import { CampaignContext } from '@context/campaign.context';
import useHeaderQuery from '@hooks/header/use-header-query';
import { HeaderDataItem } from '@interfaces/models/header';
import { getPathNameWithoutPage } from '@helpers/utils/catalog/catalog-url-helpers';
import { v4 as uuidV4 } from 'uuid';
import useCatalogPageMetadataQuery from '@hooks/catalog-context/use-catalog-page-metadata-query';

const constructCategoryKeyValuePairs = (item: HeaderDataItem, prefix = '') => {
  let keyValuePairs = {};

  if (item.localizedName) {
    const name = prefix ? `${prefix} › ${item.localizedName}` : item.localizedName;
    keyValuePairs[item.url.seo || item.url.mobile] = name;
  }

  if (item.content && item.content.length > 0) {
    for (const subItem of item.content) {
      const subKeyValuePairs = constructCategoryKeyValuePairs(
        subItem,
        prefix ? `${prefix} › ${item.localizedName}` : item.localizedName,
      );
      keyValuePairs = { ...subKeyValuePairs, ...keyValuePairs };
    }
  }

  return keyValuePairs;
};

const flattenHeaderData = (headerData: HeaderDataItem[]) =>
  (headerData ?? []).reduce(
    (accumulator, headerDataItem) => {
      const pairs = constructCategoryKeyValuePairs(headerDataItem);
      return { ...accumulator, ...pairs };
    },
    {} as Record<string, string>,
  );

const getRecentSearchUrl = () => {
  const fullUrl = window.location.href;
  const url = new URL(fullUrl);
  url.searchParams.delete('searchDate');
  url.searchParams.delete('sortBy');
  url.searchParams.delete('smartRecency');

  url.pathname = getPathNameWithoutPage();
  return url.toString().substring(fullUrl.indexOf(url.origin) + url.origin.length);
};

const useManageRecentSearches = () => {
  const { addOrEditRecentSearchEntry, recentSearches } = useRecentSearches();
  const [currentSearchId, setCurrentSearchId] = useState<string | null>(null);
  const recentSearchTitle = useRef<string>('');

  const campaign = useContextSelector(CampaignContext, (v) => v.campaign);
  const isCampaignPage = !!campaign;
  const searchQuery = useContextSelector(CatalogContext, (v) => v.searchQuery);
  const filters = useContextSelector(CatalogContext, (v) => v.filters);
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);
  const areMySizesApplicable = useContextSelector(CatalogContext, (v) => v.areMySizesApplicable);
  const filterChips = useContextSelector(CatalogContext, (v) => v.filterChips);
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const correspondingAlertId = useContextSelector(CatalogContext, (v) => v.correspondingAlertId);
  const { headerData } = useHeaderQuery(true);
  const { catalogMetadata } = useCatalogPageMetadataQuery();

  const hasQueryResult = !!queryResult;

  const getRecentSearchTitle = () => {
    if (searchQuery) {
      return searchQuery;
    }

    const pathName = getPathNameWithoutPage();
    const flattenedHeaderData = flattenHeaderData(headerData as HeaderDataItem[]);
    const headerDataName = flattenedHeaderData[pathName];
    if (pathName && headerDataName) {
      return headerDataName;
    }
    if (catalogMetadata?.content?.headline) {
      return catalogMetadata.content.headline;
    }
    return '';
  };

  const recentSearchFilterData = useMemo(() => {
    const payload = {
      q: searchQuery,
      filters,
      mySizesEnabled: areMySizesEnabled && areMySizesApplicable,
    };

    const label = filterChips
      .filter((x) => !x.isStatic)
      .map((chip) => chip.label)
      .join(' - ');

    return {
      payload,
      label,
    };
  }, [filterChips, filters, areMySizesApplicable, areMySizesEnabled, searchQuery]);

  useEffect(() => {
    if (isCampaignPage || !hasQueryResult || currentSearchId) {
      return;
    }
    // wait 1s for any URL changes on PLP mount to take place, before adding recent search
    const timeout = setTimeout(() => {
      // perform this in requestIdleCallback to avoid blocking the main thread - it's not critical to have the recent search added immediately
      window.requestIdleCallback(() => {
        const recentSearchUrl = getRecentSearchUrl();
        const matchingRecentSearch = recentSearches.find((x) => x.url === recentSearchUrl);
        if (matchingRecentSearch) {
          recentSearchTitle.current = matchingRecentSearch.title;
          setCurrentSearchId(matchingRecentSearch.id);
        } else {
          recentSearchTitle.current = getRecentSearchTitle();
          setCurrentSearchId(uuidV4());
        }
      });
    }, 1000);
    return () => clearTimeout(timeout);
  }, [hasQueryResult]);

  useEffect(() => {
    if (!currentSearchId || !recentSearchTitle.current) {
      return;
    }

    addOrEditRecentSearchEntry({
      id: currentSearchId,
      url: getRecentSearchUrl(),
      createdAt: Math.floor(Date.now() / 1000),
      title: recentSearchTitle.current,
      savedSearchId: correspondingAlertId,
      ...recentSearchFilterData,
    });
  }, [recentSearchFilterData, currentSearchId, correspondingAlertId]);
};

export default useManageRecentSearches;
